import * as React from 'react';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import { a11yProps, CustomTabPanel } from '../../../customTabs/CustomTabPanel';
import { useFetchItemFolders, useFetchAssemblyFolders } from '../../../hooks/useFetchFolders';
import FolderNodes from '../folderManager/FolderNodes';
import { getContrastTextColor } from '../../../utils/getCintrastTextColor';
import { Box, Button, useTheme } from '@mui/material';
import {
  useCreateAssemblyFolderMutation,
  useCreateItemFolderMutation,
} from '../../../hooks/useFetchFolders';
import { FolderNode } from '../../../types/FolderTypes';
import { ItemWithRelations } from '../../../bf_api';
import FoldersView from '../buildFoundryItems/FoldersView';
import { alpha, Typography } from '@mui/material';

function ItemTab() {
  //zustand states
  const theme = useTheme();
  //react-query states
  const { data: itemFolderData } = useFetchItemFolders();
  const { data: assemblyFolderData } = useFetchAssemblyFolders();
  const { mutate: createAssemblyFolder } = useCreateAssemblyFolderMutation();
  const { mutate: createItemFolder } = useCreateItemFolderMutation();

  //local state
  const [value, setValue] = React.useState(0);

  // Build Foundry Items state
  const [selectedCategoryId, setSelectedCategoryId] = React.useState<string | null>(null);
  const [selectedItems, setSelectedItems] = React.useState<ItemWithRelations[]>([]);

  const handleChange = React.useCallback((event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
  }, []);

  const handleCreateFolder = React.useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      e.preventDefault();
      e.stopPropagation();

      const folderType = value === 0 ? 'assembly' : 'item';
      const folder: FolderNode = {
        folderid: '',
        name: `${folderType === 'assembly' ? 'Assembly' : 'Item'} Folder`,
        entitytype: folderType,
        parentid: '',
        isfile: false,
        level: 1,
        isactive: true,
        lastupdated: '',
        children: [],
      };

      if (folderType === 'assembly') {
        createAssemblyFolder(folder);
      } else {
        createItemFolder(folder);
      }
    },
    [value, createAssemblyFolder, createItemFolder]
  );

  const handleCategorySelect = React.useCallback((categoryId: string | null) => {
    setSelectedCategoryId(categoryId);
  }, []);

  const handleItemSelection = React.useCallback((items: ItemWithRelations[]) => {
    setSelectedItems(items);
  }, []);

  return (
    <Box
      sx={{
        height: '100%',
        backgroundColor: theme.palette.background.paper,
        color: getContrastTextColor(theme.palette.background.paper),
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          borderBottom: 1,
          borderColor: 'divider',
          padding: '0 16px',
          position: 'sticky',
          top: 0,
          zIndex: 10,
          backgroundColor: theme.palette.background.paper,
          flexShrink: 0,
        }}
      >
        <Tabs
          value={value}
          onChange={handleChange}
          variant="scrollable"
          scrollButtons
          allowScrollButtonsMobile
          aria-label="basic tabs example"
          sx={{
            flex: 1,
            borderBottom: 1,
            borderColor: 'divider',
            '& .MuiTab-root': {
              color: getContrastTextColor(theme.palette.background.paper),
              textTransform: 'none',
              fontWeight: 500,
              minHeight: '48px',
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
              },
              '&.Mui-selected': {
                color: getContrastTextColor(theme.palette.background.paper),
                fontWeight: 600,
              },
            },
            '& .MuiTabs-indicator': {
              backgroundColor: getContrastTextColor(theme.palette.background.paper),
            },
          }}
        >
          <Tab
            label="My Assemblies"
            {...a11yProps(0)}
            aria-label="My Assemblies tab"
            title="View and manage my assemblies"
          />
          <Tab
            label="My Items"
            {...a11yProps(1)}
            aria-label="My Items tab"
            title="View and manage my items"
          />
          <Tab
            label="🌍 Community Assemblies"
            {...a11yProps(2)}
            aria-label="Community Assemblies tab"
            title="View and manage community assemblies"
          />
          <Tab
            label="🌍 Community Items"
            {...a11yProps(3)}
            aria-label="Community Items tab"
            title="View and manage community items"
          />
        </Tabs>
        <Button
          variant="outlined"
          size="small"
          onClick={handleCreateFolder}
          disabled={value === 2}
          sx={{
            color: getContrastTextColor(theme.palette.background.paper),
            borderColor: getContrastTextColor(theme.palette.background.paper),
            '&:hover': {
              borderColor: getContrastTextColor(theme.palette.background.paper),
              backgroundColor: 'rgba(0, 0, 0, 0.04)',
            },
          }}
        >
          Create {value === 0 ? 'Assembly' : 'Item'} Folder
        </Button>
      </div>
      <Box
        sx={{
          flex: 1,
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          backgroundColor: theme.palette.background.paper,
          color: getContrastTextColor(theme.palette.background.paper),
        }}
      >
        <CustomTabPanel value={value} index={0}>
          <Box sx={{ height: '100%', overflow: 'auto', padding: '16px' }}>
            {value === 0 && assemblyFolderData && (
              <FolderNodes
                context="primary"
                folderType="assembly"
                folderData={assemblyFolderData}
              />
            )}
          </Box>
        </CustomTabPanel>
        <CustomTabPanel value={value} index={1}>
          <Box sx={{ height: '100%', overflow: 'auto', padding: '16px' }}>
            {value === 1 && itemFolderData && (
              <FolderNodes context="primary" folderType="item" folderData={itemFolderData} />
            )}
          </Box>
        </CustomTabPanel>
        <CustomTabPanel value={value} index={2}>
          <Box sx={{ height: '100%', overflow: 'auto', padding: '16px' }}>
            {value === 2 && (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  alignItems: 'center',
                  height: '100%',
                  p: 4,
                  textAlign: 'center',
                }}
              >
                <Box
                  sx={{
                    maxWidth: 500,
                    p: 4,
                    borderRadius: 2,
                    background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)} 0%, ${alpha(theme.palette.secondary.main, 0.05)} 100%)`,
                    border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                    boxShadow: `0 4px 20px ${alpha(theme.palette.primary.main, 0.15)}`,
                    position: 'relative',
                    overflow: 'hidden',
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      height: '3px',
                      background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                    },
                  }}
                >
                  <Box
                    sx={{
                      fontSize: '3rem',
                      mb: 2,
                      filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.1))',
                    }}
                  >
                    🚀
                  </Box>

                  <Typography
                    variant="h4"
                    component="h2"
                    sx={{
                      fontWeight: 700,
                      color: theme.palette.primary.main,
                      mb: 2,
                      textShadow: '0 1px 2px rgba(0,0,0,0.1)',
                    }}
                  >
                    Community Assemblies
                  </Typography>

                  <Typography
                    variant="h6"
                    component="h3"
                    sx={{
                      fontWeight: 600,
                      color: theme.palette.text.primary,
                      mb: 3,
                    }}
                  >
                    Coming Soon
                  </Typography>

                  <Typography
                    variant="body1"
                    sx={{
                      color: theme.palette.text.secondary,
                      mb: 3,
                      lineHeight: 1.6,
                    }}
                  >
                    We're building a comprehensive library of pre-configured assemblies created by
                    the Unity Build community. These ready-to-use assemblies will save you hours of
                    setup time and help you bid faster.
                  </Typography>

                  <Box
                    sx={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      gap: 2,
                      justifyContent: 'center',
                      mb: 3,
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        px: 2,
                        py: 1,
                        borderRadius: 1,
                        backgroundColor: alpha(theme.palette.success.main, 0.1),
                        border: `1px solid ${alpha(theme.palette.success.main, 0.2)}`,
                      }}
                    >
                      <Box sx={{ fontSize: '1.2rem' }}>⚡</Box>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        Faster Bidding
                      </Typography>
                    </Box>

                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        px: 2,
                        py: 1,
                        borderRadius: 1,
                        backgroundColor: alpha(theme.palette.info.main, 0.1),
                        border: `1px solid ${alpha(theme.palette.info.main, 0.2)}`,
                      }}
                    >
                      <Box sx={{ fontSize: '1.2rem' }}>🤝</Box>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        Community Driven
                      </Typography>
                    </Box>

                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        px: 2,
                        py: 1,
                        borderRadius: 1,
                        backgroundColor: alpha(theme.palette.warning.main, 0.1),
                        border: `1px solid ${alpha(theme.palette.warning.main, 0.2)}`,
                      }}
                    >
                      <Box sx={{ fontSize: '1.2rem' }}>📚</Box>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        Proven Templates
                      </Typography>
                    </Box>
                  </Box>

                  <Typography
                    variant="body2"
                    sx={{
                      color: theme.palette.text.secondary,
                      fontStyle: 'italic',
                      opacity: 0.8,
                    }}
                  >
                    Currently in development - We're wrapping up our items database first to ensure
                    the best foundation for community assemblies.
                  </Typography>
                </Box>
              </Box>
            )}
          </Box>
        </CustomTabPanel>
        <CustomTabPanel value={value} index={3}>
          <Box sx={{ height: '100%', overflow: 'auto', padding: '16px' }}>
            {value === 3 && (
              <FoldersView
                selectedItems={selectedItems}
                onItemSelection={handleItemSelection}
                selectedCategoryId={selectedCategoryId}
                onCategorySelect={handleCategorySelect}
              />
            )}
          </Box>
        </CustomTabPanel>
      </Box>
    </Box>
  );
}

export default React.memo(ItemTab);
